import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useIntl } from "react-intl";
import { AutoSearch, Button, SelectInput } from "~/core";
import {
    AppHelpers,
    AuthenticationAPI,
    CustomerAPI,
    SearchAPI,
    LocalStorageHelpers,
    SkyAPI,
    urlBase,
    UserAPI,
} from "@ai360/core";
import { OrgLevelSearch } from "~/admin/agBytes/components/org-level-search";
import { useAppDispatch, useAppSelector } from "~/store/hooks";
import * as actions from "../actions";
import { getLastUsedUser } from "../last-used-user";
import {
    getFilteredCustomers,
    getLastUsedCustomerGuid,
    getLoginEmail,
    getUsers,
} from "../selectors";
import { isSsoRedirectSuppressed } from "../sso-redirect";
import { messages } from "../i18n-messages";
import { PersonalityTypes } from "~/utils/keywords";

const { LoginState } = actions;

const isConnectedType = (pid): boolean =>
    pid === PersonalityTypes.AGVANCE_STANDARD || pid === PersonalityTypes.HELENA_CORPORATE;

interface Company {
    label: string;
    value: string;
}

interface ISelectCompanyProps {
    onAction: (history) => void;
    onError: (error) => void;
    isQuickChanger: boolean;
}

const isDifferentSubdomain = (conn: SkyAPI.ISsoConnection | null): boolean => {
    if (!conn || !conn.isConnected || !conn.redirectUrl) {
        return false;
    }
    try {
        return new URL(conn.redirectUrl).host !== window.location.host;
    } catch {
        return false;
    }
};

const SelectCompany_ = (props: ISelectCompanyProps) => {
    const { isQuickChanger, onAction, onError } = props;
    const { formatMessage } = useIntl();
    const history = useHistory();
    const dispatch = useAppDispatch();

    const filteredCustomers = useAppSelector(getFilteredCustomers);
    const lastUsedCustomerGuid = useAppSelector(getLastUsedCustomerGuid);
    const theUsers = useAppSelector(getUsers);
    const loginEmail = useAppSelector(getLoginEmail);

    // Only load SSO connections when SSO is enabled for this login. getSsoStatus is cached
    // by RTK Query (login-form already called it during login and fetchLoginUser populates
    // loginEmail on reload), so this reuses the cached result rather than re-requesting.
    const {
        data: ssoStatus,
        isFetching: isSsoStatusFetching = false,
        isUninitialized: isSsoStatusUninitialized = false,
    } = SkyAPI.useGetSsoStatusQuery(loginEmail, {
        skip: !loginEmail,
    });
    const skipConnections = theUsers.length === 0 || !ssoStatus?.ssoEnabled;
    const {
        data: connections = [],
        isFetching: isConnectionsFetching = false,
        isUninitialized: isConnectionsUninitialized = false,
    } = SkyAPI.useGetSsoConnectionsQuery(undefined, {
        skip: skipConnections,
    });

    const isSsoInfoPending =
        (!!loginEmail && (isSsoStatusUninitialized || isSsoStatusFetching)) ||
        (!skipConnections && (isConnectionsUninitialized || isConnectionsFetching));

    const lastUsedUser = getLastUsedUser(theUsers);
    const [selectedCompanyGuid, setSelectedCompanyGuid] = useState<string>(
        lastUsedUser ? lastUsedUser.lastUsedCompanyGuid : null
    );
    const [selectedLocationGuid, setSelectedLocationGuid] = useState<string>(
        lastUsedUser ? lastUsedUser.lastUsedLocationGuid : null
    );
    const [selectedCustomerGuid, setSelectedCustomerGuid] = useState<string>(
        lastUsedUser ? lastUsedUser.lastUsedCustomerGuid : null
    );

    // When SSO is enabled, hide connected-type companies (AGVANCE_STANDARD/HELENA_CORPORATE)
    // that have no matching connected connection. DISCONNECTED and any other type always show.
    // When SSO is not enabled, show ALL companies unfiltered. Only the displayed list is
    // filtered; all other logic still reads from the full theUsers by companyGuid.
    const visibleUsers = useMemo(
        () =>
            !ssoStatus?.ssoEnabled
                ? theUsers
                : theUsers.filter(
                      (u) =>
                          !isConnectedType(u.ownerPersonalityId) ||
                          connections.some((c) => c.orgLevelGuid === u.companyGuid && c.isConnected)
                  ),
        [theUsers, ssoStatus?.ssoEnabled, connections]
    );

    const companies = useMemo<Company[]>(
        () =>
            visibleUsers.map((u) => ({
                value: u.companyGuid,
                label: u.companyName,
            })),
        [visibleUsers]
    );

    const updateCustomerList = (companyGuid: string, locationGuid: string) => {
        const selectedUser = !companyGuid
            ? null
            : theUsers.find((user) => user.companyGuid === companyGuid);

        if (companyGuid && selectedUser) {
            if (locationGuid) {
                dispatch(actions.fetchFilteredCustomerList(locationGuid, selectedUser.userGuid));
                return;
            } else {
                dispatch(actions.fetchFilteredCustomerList(companyGuid, selectedUser.userGuid));
                return;
            }
        }
    };

    // Constructor side effect: load the customer list for the initially-applicable company on mount.
    useEffect(() => {
        const applicableCompanyGuid = selectedCompanyGuid
            ? selectedCompanyGuid
            : companies.length === 1
            ? companies[0].value
            : "";
        updateCustomerList(applicableCompanyGuid, selectedLocationGuid);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const _handleError = (error) => {
        if (onError) {
            onError(error);
        }
    };

    const selectedConnection = (): SkyAPI.ISsoConnection | null => {
        return (connections || []).find((c) => c.orgLevelGuid === selectedCompanyGuid) || null;
    };

    const setUserAndNavHome = async (theUser: UserAPI.IUser) => {
        let locationGuid = selectedLocationGuid;
        if (onAction) {
            dispatch(actions.setLoginState(LoginState.NONE));
            onAction(history);
        }

        let customerName = "";
        if (selectedCustomerGuid) {
            const customerModel = await CustomerAPI.getCustomer({
                UserGuid: theUser.userGuid,
                Model: selectedCustomerGuid,
            });
            customerName =
                customerModel.customerAliasName == null || customerModel.customerAliasName == ""
                    ? customerModel.name
                    : customerModel.customerAliasName;

            if (!locationGuid) {
                locationGuid = customerModel.customerOrgLevelList[0].orgLevelGuid;
            }
        }
        dispatch(
            actions.setUserInfo(
                {
                    ...theUser,
                    lastUsedCompanyGuid: selectedCompanyGuid,
                    lastUsedLocationGuid: locationGuid,
                    lastUsedCustomerGuid: selectedCustomerGuid,
                    lastUsedCustomerName: customerName,
                },
                selectedCompanyGuid,
                locationGuid,
                selectedCustomerGuid,
                isQuickChanger
            )
        );

        if (!onAction) {
            dispatch(actions.setLoginState(LoginState.LOGIN_FORM));
            history.replace(urlBase);
        }
    };

    const login = () => {
        if (!selectedCompanyGuid) {
            _handleError(formatMessage(messages.selectCompany));
            return;
        }
        const theUser = theUsers.find((u) => u.companyGuid === selectedCompanyGuid);
        if (!theUser) {
            _handleError(formatMessage(messages.selectCompany));
            return;
        }
        const conn = selectedConnection();
        if (isDifferentSubdomain(conn) && !isSsoRedirectSuppressed()) {
            LocalStorageHelpers.set(LocalStorageHelpers.COMPANY_GUID, selectedCompanyGuid);
            AuthenticationAPI.SetLastUsedLogin(theUser.userGuid, selectedCompanyGuid, null, null)
                .then(() => {
                    window.location.href = conn.redirectUrl;
                })
                .catch((err) => _handleError(err));
            return;
        }
        LocalStorageHelpers.set(LocalStorageHelpers.COMPANY_GUID, selectedCompanyGuid);
        setUserAndNavHome(theUser);
    };

    const setCompany = (companyGuid: string) => {
        setSelectedCompanyGuid(companyGuid);
        setSelectedLocationGuid(null);
        setSelectedCustomerGuid(null);
        updateCustomerList(companyGuid, null);
    };

    const setCustomer = (customer: SearchAPI.ILegacyCustomerResult) => {
        setSelectedCustomerGuid(customer ? customer.customerGuid : null);

        if (!selectedLocationGuid && customer) {
            setSelectedLocationGuid(customer.orgLevelGuid);
        }
    };

    const setLocation = (location) => {
        setSelectedLocationGuid(location ? location.orgLevelGuid : null);
        setSelectedCustomerGuid(null);
        const selectedUser = !selectedCompanyGuid
            ? null
            : theUsers.find((user) => user.companyGuid === selectedCompanyGuid);
        if (location) {
            dispatch(
                actions.fetchFilteredCustomerList(location.orgLevelGuid, selectedUser.userGuid)
            );
        } else {
            dispatch(actions.fetchFilteredCustomerList(selectedCompanyGuid, selectedUser.userGuid));
        }
    };

    const selectedUser = !selectedCompanyGuid
        ? null
        : theUsers.find((user) => user.companyGuid === selectedCompanyGuid);
    const orgLevelList =
        !selectedUser || !(selectedUser.role && selectedUser.role.orgLevelQuickChanger)
            ? []
            : AppHelpers.parseOrgLevelList(
                  selectedUser.orgLevelList,
                  "orgLevelParents",
                  " > ",
                  null
              );
    const hideCustomerList = filteredCustomers.length < 1;
    const excludedCustomers = lastUsedCustomerGuid == null ? [] : [lastUsedCustomerGuid];
    const hideOrgFields = isDifferentSubdomain(selectedConnection()) && !isSsoRedirectSuppressed();

    // Login flow only: a settled-but-empty company list means the user has no access to
    // any company (none returned, or SSO filtered them all out) — the only way forward
    // is logging out.
    if (!isQuickChanger && !isSsoInfoPending && companies.length === 0) {
        return (
            <div>
                <div className="login-input-container">
                    <div className="display-label">{formatMessage(messages.noCompanyAccess)}</div>
                </div>
                <div className="login-center">
                    <Button
                        tabIndex={1}
                        className="login-btn"
                        value={formatMessage(messages.logOut)}
                        onClick={() => dispatch(actions.logoutUser())}
                    />
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className="login-input-container">
                {isQuickChanger ? null : (
                    <div className="display-label">{formatMessage(messages.selectCompany)}</div>
                )}
                {isQuickChanger && theUsers.length === 1 ? null : (
                    <SelectInput
                        tabIndex={1}
                        autoFocus
                        openOnFocus={false}
                        options={companies}
                        value={selectedCompanyGuid}
                        placeholderText={formatMessage(messages.companyNameLbl)}
                        onChange={(value) => setCompany(value)}
                    />
                )}
                {orgLevelList.length <= 1 || hideOrgFields ? null : (
                    <OrgLevelSearch
                        clearOnSelection={true}
                        itemList={orgLevelList}
                        onSelection={(v) => setLocation(v)}
                        onSearchChange={(v) => {
                            if (!v) {
                                setLocation(null);
                            }
                        }}
                        placeholderText={formatMessage(messages.locationText)}
                        selectedValue={selectedLocationGuid}
                    />
                )}
                {hideCustomerList || hideOrgFields ? null : (
                    <AutoSearch
                        clearOnFocus={true}
                        clearOnSelection={true}
                        itemList={filteredCustomers}
                        excludedValues={excludedCustomers}
                        keyProp={"customerGuid"}
                        nameProp={"customerName"}
                        onSearchChange={(v) => {
                            if (!v) {
                                setCustomer(null);
                            }
                        }}
                        onSelection={(item: SearchAPI.ILegacyCustomerResult) => setCustomer(item)}
                        placeholderText={"Customer"}
                        selectedValue={selectedCustomerGuid}
                        showTopLabel={true}
                    />
                )}
            </div>
            <div className="login-center">
                <Button tabIndex={2} className="login-btn" type="login" onClick={() => login()} />
            </div>
        </div>
    );
};

export const SelectCompany = SelectCompany_;

export default SelectCompany_;
